import React, { useState, useRef, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { useCurrency } from '../context/CurrencyContext';
import { ChevronDown, Globe, Languages, Coins } from 'lucide-react';

// Country/Region data
const REGIONS = {
  GB: { name: 'United Kingdom', code: 'GB' },
  US: { name: 'United States', code: 'US' },
  FR: { name: 'France', code: 'FR' },
  DE: { name: 'Germany', code: 'DE' },
  ES: { name: 'Spain', code: 'ES' },
  IT: { name: 'Italy', code: 'IT' },
  JP: { name: 'Japan', code: 'JP' },
  AU: { name: 'Australia', code: 'AU' },
  CA: { name: 'Canada', code: 'CA' },
  NL: { name: 'Netherlands', code: 'NL' },
  BE: { name: 'Belgium', code: 'BE' },
  IE: { name: 'Ireland', code: 'IE' },
  AT: { name: 'Austria', code: 'AT' },
  CH: { name: 'Switzerland', code: 'CH' },
  SE: { name: 'Sweden', code: 'SE' },
  DK: { name: 'Denmark', code: 'DK' },
  NO: { name: 'Norway', code: 'NO' },
  PL: { name: 'Poland', code: 'PL' },
  PT: { name: 'Portugal', code: 'PT' },
  NZ: { name: 'New Zealand', code: 'NZ' },
};

// Currency data
const CURRENCIES = {
  GBP: { symbol: '£', name: 'British Pound', code: 'GBP' },
  USD: { symbol: '$', name: 'US Dollar', code: 'USD' },
  EUR: { symbol: '€', name: 'Euro', code: 'EUR' },
  JPY: { symbol: '¥', name: 'Japanese Yen', code: 'JPY' },
  AUD: { symbol: 'A$', name: 'Australian Dollar', code: 'AUD' },
  CAD: { symbol: 'C$', name: 'Canadian Dollar', code: 'CAD' },
  CHF: { symbol: 'CHF', name: 'Swiss Franc', code: 'CHF' },
  SEK: { symbol: 'kr', name: 'Swedish Krona', code: 'SEK' },
  NOK: { symbol: 'kr', name: 'Norwegian Krone', code: 'NOK' },
  DKK: { symbol: 'kr', name: 'Danish Krone', code: 'DKK' },
  PLN: { symbol: 'zł', name: 'Polish Zloty', code: 'PLN' },
};

// Flag component using Flagcdn.com
const FlagIcon = ({ code, size = 'md' }) => {
  const sizeClass = size === 'sm' ? 'w-5 h-4' : size === 'lg' ? 'w-10 h-8' : 'w-6 h-5';
  return (
    <img 
      src={`https://flagcdn.com/w40/${code.toLowerCase()}.png`}
      srcSet={`https://flagcdn.com/w80/${code.toLowerCase()}.png 2x`}
      alt={code}
      className={`${sizeClass} object-cover rounded-sm inline-block`}
      loading="lazy"
    />
  );
};

const SettingsSelector = ({ className = '' }) => {
  const { language: contextLanguage, setLanguage: setContextLanguage, languages } = useLanguage();
  const { currency: contextCurrency, setCurrency: setContextCurrency, isLiveRates, lastUpdated, exchangeRates } = useCurrency();
  const [isOpen, setIsOpen] = useState(false);
  const [region, setRegion] = useState('GB');
  const [currency, setCurrency] = useState('GBP');
  const [localLanguage, setLocalLanguage] = useState('en');
  const [activeTab, setActiveTab] = useState('region');
  const dropdownRef = useRef(null);

  // Load saved preferences on mount
  useEffect(() => {
    try {
      const savedRegion = localStorage.getItem('preferredRegion');
      const savedLanguage = localStorage.getItem('preferredLanguage');
      if (savedRegion && REGIONS[savedRegion]) setRegion(savedRegion);
      if (savedLanguage && languages && languages[savedLanguage]) setLocalLanguage(savedLanguage);
    } catch (e) {}
  }, [languages]);

  // Sync local language with context
  useEffect(() => {
    if (contextLanguage) {
      setLocalLanguage(contextLanguage);
    }
  }, [contextLanguage]);

  // Sync local currency with context
  useEffect(() => {
    if (contextCurrency) {
      setCurrency(contextCurrency);
    }
  }, [contextCurrency]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Save region
  const handleRegionClick = (code) => {
    setRegion(code);
    try { localStorage.setItem('preferredRegion', code); } catch(e) {}
  };

  // Save language - update local state first, then context
  const handleLanguageClick = (code) => {
    console.log('Language clicked:', code);
    setLocalLanguage(code);
    try { 
      localStorage.setItem('preferredLanguage', code);
      // Also update context
      if (setContextLanguage) {
        setContextLanguage(code);
      }
    } catch(e) {
      console.log('Error saving language:', e);
    }
  };

  // Save currency - update local state and context
  const handleCurrencyClick = (code) => {
    console.log('Currency clicked:', code);
    setCurrency(code);
    try { 
      localStorage.setItem('preferredCurrency', code);
      if (setContextCurrency) {
        setContextCurrency(code);
      }
    } catch(e) {
      console.log('Error saving currency:', e);
    }
  };

  const currentRegion = REGIONS[region] || REGIONS['GB'];
  const currentLang = languages ? languages[localLanguage] : { name: 'English' };
  const currentCurrency = CURRENCIES[currency] || CURRENCIES['GBP'];

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Main Button - Circular to match other icons */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="group flex items-center justify-center w-12 h-12 bg-white border border-gray-300 rounded-full text-black hover:bg-black hover:text-white hover:border-black transition-all cursor-pointer"
        data-testid="settings-selector"
      >
        <FlagIcon code={currentRegion.code} size="md" />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-80 bg-white border border-gray-200 rounded-xl shadow-xl z-[100] overflow-hidden">
          {/* Header */}
          <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
            <h3 className="text-sm font-semibold text-gray-900">Update your settings</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Set where you live, what language you speak and the currency you use.
            </p>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-gray-200" style={{ position: 'relative', zIndex: 1 }}>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setActiveTab('region'); }}
              className={`flex-1 px-4 py-2.5 text-xs font-medium transition-colors ${
                activeTab === 'region' 
                  ? 'text-black border-b-2 border-black bg-white' 
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
            >
              <Globe className="h-3.5 w-3.5 inline mr-1.5" />
              Region
            </button>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setActiveTab('language'); }}
              className={`flex-1 px-4 py-2.5 text-xs font-medium transition-colors ${
                activeTab === 'language' 
                  ? 'text-black border-b-2 border-black bg-white' 
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
            >
              <Languages className="h-3.5 w-3.5 inline mr-1.5" />
              Language
            </button>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setActiveTab('currency'); }}
              className={`flex-1 px-4 py-2.5 text-xs font-medium transition-colors ${
                activeTab === 'currency' 
                  ? 'text-black border-b-2 border-black bg-white' 
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
            >
              <Coins className="h-3.5 w-3.5 inline mr-1.5" />
              Currency
            </button>
          </div>

          {/* Content */}
          <div className="max-h-64 overflow-y-auto">
            {/* Region Tab */}
            {activeTab === 'region' && (
              <div className="py-1">
                {Object.entries(REGIONS).map(([code, data]) => (
                  <button
                    key={code}
                    type="button"
                    onClick={(e) => { e.stopPropagation(); handleRegionClick(code); }}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm text-left hover:bg-gray-100 transition-colors cursor-pointer ${
                      region === code ? 'bg-gray-100' : ''
                    }`}
                  >
                    <FlagIcon code={code} size="sm" />
                    <span className={`flex-1 text-black ${region === code ? 'font-semibold' : ''}`}>{data.name}</span>
                    {region === code && <span className="text-green-600">✓</span>}
                  </button>
                ))}
              </div>
            )}

            {/* Language Tab */}
            {activeTab === 'language' && (
              <div className="py-1">
                {languages && Object.entries(languages).map(([code, lang]) => (
                  <button
                    key={code}
                    type="button"
                    onClick={(e) => { e.stopPropagation(); handleLanguageClick(code); }}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm text-left hover:bg-gray-100 transition-colors cursor-pointer ${
                      localLanguage === code ? 'bg-gray-100' : ''
                    }`}
                  >
                    <span className="w-6 text-center font-medium text-gray-500">{code.toUpperCase()}</span>
                    <span className={`flex-1 text-black ${localLanguage === code ? 'font-semibold' : ''}`}>{lang.name}</span>
                    {localLanguage === code && <span className="text-green-600">✓</span>}
                  </button>
                ))}
              </div>
            )}

            {/* Currency Tab */}
            {activeTab === 'currency' && (
              <div className="py-1">
                {/* Live rates indicator */}
                <div className="px-4 py-2 bg-gray-50 border-b border-gray-100">
                  <div className="flex items-center gap-2 text-xs">
                    {isLiveRates ? (
                      <>
                        <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                        <span className="text-green-700 font-medium">Live rates</span>
                        {lastUpdated && (
                          <span className="text-gray-500">
                            · Updated {lastUpdated.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        )}
                      </>
                    ) : (
                      <>
                        <span className="w-2 h-2 bg-yellow-500 rounded-full"></span>
                        <span className="text-yellow-700">Using cached rates</span>
                      </>
                    )}
                  </div>
                </div>
                {Object.entries(CURRENCIES).map(([code, curr]) => (
                  <button
                    key={code}
                    type="button"
                    onClick={(e) => { e.stopPropagation(); handleCurrencyClick(code); }}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm text-left hover:bg-gray-100 transition-colors cursor-pointer ${
                      currency === code ? 'bg-gray-100' : ''
                    }`}
                  >
                    <span className="w-6 text-center font-medium text-gray-600">{curr.symbol}</span>
                    <span className={`flex-1 text-black ${currency === code ? 'font-semibold' : ''}`}>{curr.name} ({curr.code})</span>
                    {/* Show exchange rate */}
                    {code !== 'GBP' && exchangeRates && exchangeRates[code] && (
                      <span className="text-xs text-gray-400">1 GBP = {exchangeRates[code].toFixed(2)}</span>
                    )}
                    {currency === code && <span className="text-green-600 ml-2">✓</span>}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Footer Summary */}
          <div className="px-4 py-3 bg-gray-50 border-t border-gray-200">
            <div className="flex items-center text-xs text-gray-600 gap-3">
              <span className="flex items-center gap-1">
                <FlagIcon code={currentRegion.code} size="sm" />
                <span>{currentRegion.name}</span>
              </span>
              <span className="text-gray-300">|</span>
              <span>{currentLang?.name || 'English'}</span>
              <span className="text-gray-300">|</span>
              <span>{currentCurrency.symbol} {currentCurrency.code}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SettingsSelector;
